import React, {
  useCallback,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import TextField from 'apollo-react/components/TextField';
import Button from 'apollo-react/components/Button';
import Typography from '../../../../StyledTypography';
import TrashIcon from 'apollo-react-icons/Trash';
import PlusIcon from 'apollo-react-icons/Plus';
import FieldWrapper from '../../fields/FieldWrapper';
import HelperText from '../../fields/HelperText';
import IDSField from '../IDSField';
import { FIELD_CATEGORIES } from '../fieldRegistry';
import styles from '../IDSField.module.css';

const getValueFromEventOrValue = (eventOrValue) => {
  if (eventOrValue == null) return '';
  if (
    typeof eventOrValue === 'object' &&
    'target' in eventOrValue &&
    eventOrValue.target &&
    typeof eventOrValue.target === 'object' &&
    'value' in eventOrValue.target
  ) {
    return eventOrValue.target.value ?? '';
  }
  return eventOrValue;
};

const cloneRepeaterValue = (value) => {
  if (!value || typeof value !== 'object') return value;
  if (value instanceof Date || value instanceof File) return value;
  if (Array.isArray(value)) return value.map(cloneRepeaterValue);
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch (error) {
      // Fall back to JSON clone
    }
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const getDefaultRepeaterItemValues = (fields) => {
  if (!Array.isArray(fields)) return {};
  return fields.reduce((acc, field) => {
    if (
      field.defaultValue !== undefined &&
      field.defaultValue !== null &&
      field.defaultValue !== ''
    ) {
      acc[field.id] = field.defaultValue;
      return acc;
    }
    if (
      field.value !== undefined &&
      field.value !== null &&
      field.value !== ''
    ) {
      acc[field.id] = field.value;
    }
    return acc;
  }, {});
};

const getRepeaterItemFieldValue = (item, fieldId) => {
  if (!item || typeof item !== 'object') return '';
  if (fieldId in item) return item[fieldId];
  if (
    item.value &&
    typeof item.value === 'object' &&
    item.value !== null &&
    fieldId in item.value
  ) {
    return item.value[fieldId];
  }
  return '';
};

const RepeaterItemField = ({
  field,
  itemKey,
  value,
  onChange,
  onBlur,
  disabled,
  error = '',
  touched = false,
  // Props for nested repeater child validation
  allErrors = {},
  allTouched = {},
  errorPathPrefix,
  // File-related props
  existingFiles,
  onServerFileDelete,
  formId,
  subformId,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled,
  initialStagedFiles,
  enablePendingDelete = false,
  // Ref callback for file fields (imperative confirmPendingDeletions)
  fileRef,
}) => {
  const fieldForRender = useMemo(
    () => ({
      ...field,
      id: `${field.id}-${itemKey}`,
    }),
    [field, itemKey]
  );

  // Filter existing files for this specific repeater item field
  const fieldExistingFiles = useMemo(() => {
    if (!existingFiles || !Array.isArray(existingFiles)) return [];

    // Construct possible field ID patterns (with string conversion for safety)
    const baseFieldId = String(field.id);
    const stringItemKey = String(itemKey);
    const expectedFieldId = `${baseFieldId}-${stringItemKey}`;
    // Also try with __item_ prefix (alternative format)
    const altFieldId = `${baseFieldId}__item_${stringItemKey}`;

    return existingFiles.filter((f) => {
      const fileFieldId = String(
        f.fieldId || f.field_id || f.context?.field_id || ''
      );
      return fileFieldId === expectedFieldId || fileFieldId === altFieldId;
    });
  }, [existingFiles, field.id, itemKey]);

  // Filter initial staged files for this specific repeater item field
  const fieldInitialStagedFiles = useMemo(() => {
    if (!initialStagedFiles || !Array.isArray(initialStagedFiles)) return [];

    // Construct possible field ID patterns (with string conversion for safety)
    const baseFieldId = String(field.id);
    const stringItemKey = String(itemKey);
    const expectedFieldId = `${baseFieldId}-${stringItemKey}`;
    // Also try with __item_ prefix (alternative format used by some backends)
    const altFieldId = `${baseFieldId}__item_${stringItemKey}`;

    return initialStagedFiles.filter((f) => {
      const fileFieldId = String(
        f.fieldId || f.field_id || f.context?.field_id || ''
      );
      return fileFieldId === expectedFieldId || fileFieldId === altFieldId;
    });
  }, [initialStagedFiles, field.id, itemKey]);

  return (
    <IDSField
      field={fieldForRender}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
      touched={touched}
      allErrors={allErrors}
      allTouched={allTouched}
      errorPathPrefix={errorPathPrefix}
      existingFiles={fieldExistingFiles}
      onServerFileDelete={onServerFileDelete}
      formId={formId}
      subformId={subformId}
      onStagedFileUpload={onStagedFileUpload}
      onStagedFileDelete={onStagedFileDelete}
      eagerUploadEnabled={eagerUploadEnabled}
      initialStagedFiles={fieldInitialStagedFiles}
      enablePendingDelete={enablePendingDelete}
      fileRef={fileRef}
    />
  );
};

const RepeaterFieldInput = forwardRef(
  (
    {
      label,
      value,
      onChange,
      disabled,
      required,
      columnStyle,
      helperText,
      showError,
      fields,
      onBlur,
      fieldId,
      allErrors = {},
      allTouched = {},
      errorPathPrefix,
      // File-related props
      existingFiles = [],
      onServerFileDelete,
      formId,
      subformId,
      onStagedFileUpload,
      onStagedFileDelete,
      eagerUploadEnabled = false,
      initialStagedFiles = [],
      enablePendingDelete = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const items = useMemo(() => (Array.isArray(value) ? value : []), [value]);

    // Collect refs from all nested file fields for imperative confirm/cancel
    const fileRefsMap = useRef({});

    // Helper: iterate all collected file refs and call a method
    const forEachFileRef = useCallback((methodName) => {
      const promises = [];
      Object.values(fileRefsMap.current).forEach((fileRef) => {
        if (fileRef && typeof fileRef[methodName] === 'function') {
          const result = fileRef[methodName]();
          if (result && typeof result.then === 'function') {
            promises.push(result);
          }
        }
      });
      return Promise.all(promises);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        confirmPendingDeletions: () =>
          forEachFileRef('confirmPendingDeletions'),
        cancelPendingDeletions: () => forEachFileRef('cancelPendingDeletions'),
        cancelPendingUploads: () => forEachFileRef('cancelPendingUploads'),
        hasFiles: () =>
          Object.values(fileRefsMap.current).some(
            (fileRef) =>
              fileRef &&
              typeof fileRef.hasFiles === 'function' &&
              fileRef.hasFiles()
          ),
        hasFilesForRef: (refKey) => {
          const fileRef = fileRefsMap.current[refKey];
          return (
            fileRef &&
            typeof fileRef.hasFiles === 'function' &&
            fileRef.hasFiles()
          );
        },
      }),
      [forEachFileRef]
    );

    const nestedFields = useMemo(
      () => (Array.isArray(fields) ? fields : []),
      [fields]
    );
    const hasNestedFields = nestedFields.length > 0;

    const handleAddItem = useCallback(() => {
      const defaultValues = hasNestedFields
        ? getDefaultRepeaterItemValues(nestedFields)
        : {};
      const newItem = hasNestedFields
        ? { id: Date.now(), ...defaultValues }
        : { id: Date.now(), value: '' };
      onChange([...items, newItem]);
    }, [items, onChange, hasNestedFields, nestedFields]);

    const handleRemoveItem = useCallback(
      (index) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems);
      },
      [items, onChange]
    );

    const handleItemChange = useCallback(
      (index, newValue) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], value: newValue };
        onChange(newItems);
      },
      [items, onChange]
    );

    const basePath = errorPathPrefix || fieldId;

    const handleNestedFieldChange = useCallback(
      (index, fieldId, newValue) => {
        const normalizedValue = cloneRepeaterValue(
          getValueFromEventOrValue(newValue)
        );
        const newItems = [...items];
        const currentItem =
          newItems[index] && typeof newItems[index] === 'object'
            ? newItems[index]
            : {};
        newItems[index] = {
          ...currentItem,
          [fieldId]: normalizedValue,
        };
        onChange(newItems);
      },
      [items, onChange]
    );

    return (
      <FieldWrapper style={columnStyle}>
        <Typography variant="bodySmall" className={styles.repeaterLabel}>
          {label}
          {required && <span className={styles.requiredIndicator}> *</span>}
        </Typography>
        <div className={styles.repeaterContainer}>
          {items.length === 0 ? (
            <div className={styles.repeaterEmptyState}>
              <Typography variant="bodySmall" color="textSecondary">
                {t('formRenderer.repeaterNoItems')}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                className={styles.repeaterEmptyStateHint}
              >
                {t('formRenderer.repeaterAddHint')}
              </Typography>
            </div>
          ) : (
            items.map((item, index) => (
              <div key={item.id || index}>
                <div className={styles.repeaterItemHeader}>
                  <Typography
                    variant="bodySmall"
                    className={styles.repeaterItemTitle}
                  >
                    {t('formRenderer.repeaterItem', { index: index + 1 })}
                  </Typography>
                  {!disabled && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleRemoveItem(index)}
                      className={styles.repeaterRemoveButton}
                    >
                      <TrashIcon className={styles.iconSpacingSmall} />
                      {t('formRenderer.repeaterRemove')}
                    </Button>
                  )}
                </div>
                <div className={styles.repeaterItem}>
                  {hasNestedFields ? (
                    <div className={styles.repeaterItemFields}>
                      {nestedFields.map((field) => {
                        const isFileField = FIELD_CATEGORIES.FILE.includes(
                          field.type
                        );
                        const refKey = `${field.id}-${item.id || index}`;
                        const childErrorKey = basePath
                          ? `${basePath}[${index}].${field.id}`
                          : '';
                        return (
                          <RepeaterItemField
                            key={refKey}
                            field={field}
                            itemKey={item.id || index}
                            value={getRepeaterItemFieldValue(item, field.id)}
                            onChange={(newValue) =>
                              handleNestedFieldChange(index, field.id, newValue)
                            }
                            onBlur={onBlur}
                            disabled={disabled}
                            error={
                              childErrorKey
                                ? allErrors[childErrorKey] || ''
                                : ''
                            }
                            touched={
                              childErrorKey
                                ? !!allTouched[childErrorKey]
                                : false
                            }
                            allErrors={allErrors}
                            allTouched={allTouched}
                            errorPathPrefix={childErrorKey}
                            existingFiles={existingFiles}
                            onServerFileDelete={onServerFileDelete}
                            formId={formId}
                            subformId={subformId}
                            onStagedFileUpload={onStagedFileUpload}
                            onStagedFileDelete={onStagedFileDelete}
                            eagerUploadEnabled={eagerUploadEnabled}
                            initialStagedFiles={initialStagedFiles}
                            enablePendingDelete={enablePendingDelete}
                            fileRef={
                              isFileField
                                ? (node) => {
                                    if (node) {
                                      fileRefsMap.current[refKey] = node;
                                    } else {
                                      delete fileRefsMap.current[refKey];
                                    }
                                  }
                                : undefined
                            }
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <TextField
                      value={item.value || ''}
                      onChange={(e) => handleItemChange(index, e.target.value)}
                      onBlur={onBlur}
                      disabled={disabled}
                      fullWidth
                      label={t('formRenderer.repeaterEnterValue')}
                      placeholder={t('formRenderer.repeaterTypeHere')}
                    />
                  )}
                </div>
              </div>
            ))
          )}
          {!disabled && (
            <div className={styles.repeaterAddButtonContainer}>
              <Button
                variant="secondary"
                onClick={handleAddItem}
                fullWidth
                className={styles.repeaterAddButton}
              >
                <PlusIcon className={styles.iconSpacing} />{' '}
                {t('formRenderer.repeaterAddItem')}
              </Button>
            </div>
          )}
        </div>
        <HelperText text={helperText} error={showError} />
      </FieldWrapper>
    );
  }
);

RepeaterFieldInput.displayName = 'RepeaterFieldInput';

export default RepeaterFieldInput;
